import React from "react";

const PromotionPsychologyFunnelVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
This lesson argues promotions are driven by human psychology, not pure merit. It introduces ten decision levers that quietly tilt outcomes, like familiarity, halo, reciprocity, ownership, social proof, anchoring, scarcity, authority, and framing credit. The focus is not manipulation but awareness, using consistent signals so your name triggers trust, recall, and low risk in calibration rooms, where decisions are made by brains, not spreadsheets.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-4 space-y-4 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>
      If you strip promotions down to their core, they’re not merit decisions.
      They’re psychological ones, made by human brains, inside corporate
      systems.
    </p>

    <p>
      The difference between the one who moves up and the one who stays?
      <br />
      One understands <em>how those brains really make decisions.</em>
    </p>

    <p>
      Let’s look at 10 levers, the psychological switches that silently tilt
      promotion decisions. These aren’t manipulation tricks. They’re just
      human nature, used consciously.
    </p>

    <p>
      <strong>1.&nbsp; MERE-EXPOSURE EFFECT</strong>
    </p>

    <p>
      Familiarity breeds comfort. People trust what they see often. Repeated,
      neutral exposure reduces perceived risk.
    </p>

    <p>
      Be consistently visible in rooms, mails, and updates that matter to
      decision-makers. Drop short, periodic updates instead of long quarterly
      decks. Attend recurring leadership syncs as an “observer who adds
      value.”
    </p>

    <p>
      <strong>THE GOAL:</strong> When your name comes up in calibration, they
      shouldn’t ask <em>“Who?”</em> They should nod and go,{" "}
      “Oh yeah, I know them.”
    </p>

    <p>
      <strong>2.&nbsp; HALO EFFECT</strong>
    </p>

    <p>
      One strong trait colors everything. If you’re rock-solid in one thing,
      people assume you’re solid in all things.
    </p>

    <p>
      You don’t need ten superpowers. You need{" "}
      <em>one consistent signature behavior.</em>
    </p>

    <p className="mt-4">
      Pick one. Own it. Be so consistent that people can count on it.
    </p>

    <p>
      <strong>THE GOAL:</strong> You don't need to be excellent at everything.
      You need one consistent trait that brands you.
    </p>

    <p>
      And here’s the part most people get wrong: they obsess over fixing
      weaknesses.
    </p>

    <p>
      But the smarter move? Play to your strengths. Perfect what you’re already
      good at. When you amplify that one standout trait, the halo effect kicks
      in, and most of your weaknesses get quietly covered.
    </p>

    <p>
      <strong>3.&nbsp; RECIPROCITY BIAS</strong>
    </p>

    <p>
      Human beings hate unfinished transactions. You make someone’s life
      easier, their brain writes you a quiet IOU.
    </p>

    <p>
      Send frameworks, formats, or one-liners that save them time or make them
      look good. No drama. No bragging. Just value.
    </p>

    <p>
      Here’s a simple example of how this works in real life:
    </p>

    <p>
      An elderly gentleman once asked someone to switch seats on a flight. He
      agreed, and when the man thanked him, he didn’t just shrug it off, he
      said, “I’m sure you would’ve done the same.”
    </p>

    <p>
      At that moment, the gentleman paused and asked, “Can I do something for you?”
    </p>

    <p>
      That’s reciprocity bias. When you help, people feel an instinctive need
      to balance the scale.
    </p>

    <p>
      When someone says thank you, don’t wave it away, add a line that makes
      the contribution register. Tiny cues like this help the IOU stick.
    </p>

    <p>
      <strong>THE GOAL:</strong> When your name comes up for promotion, that IOU
      triggers. Helping you is now the fair thing to do.
    </p>

    <p>
      <strong>4.&nbsp; ENDOWMENT EFFECT</strong>
    </p>

    <p>
      People protect what they own. When leaders name, co-shape, or present
      what you build, it becomes theirs.
    </p>

    <p>
      Once they’re attached, they’ll defend both the project <em>and you.</em>
    </p>

    <p>
      Here’s how you use it:
    </p>

    <p>
      Don’t ask for feedback after the work is finished, that turns them into
      a judge.
    </p>

    <p>
      Ask <em>during the process.</em> Bring them in early. A small suggestion,
      a tiny tweak, even one line of input makes them a co-creator.
    </p>

    <p>
      And once someone helps build something, they’re invested. They’ll stand
      by it because part of it, and part of <em>you</em>, feels like theirs.
    </p>

    <p>
      <strong>THE GOAL:</strong> Make your work their baby. They’ll fight to
      keep it, and you.
    </p>

    <p>
      <strong>5.&nbsp; SOCIAL PROOF / BANDWAGON EFFECT</strong>
    </p>

    <p>
      If others see your value, your stakeholder stops second-guessing it.
      That’s social proof, crowd validation and everybody starts jumping on
      the bandwagon.
    </p>

    <p>
      Collect lightweight, public credibility cues.
    </p>

    <p>
      Tag senior leaders in appreciation. Volunteer for visible cross-team
      efforts, where multiple stakeholders see your work. Get peers to mention
      your name positively in their updates.
    </p>

    <p>
      <strong>THE GOAL:</strong> Be the name everyone’s already heard good
      things about. Consensus is comforting, especially in promotions.
    </p>

    <p>
      <strong>6.&nbsp; ANCHORING BIAS</strong>
    </p>

    <p>
      People judge you based on the first reference point their brain grabs,
      the anchor. So your performance is never evaluated in isolation. It’s
      evaluated relative to what’s around you.
    </p>

    <p>
      If your peers are weak, don’t complain, that contrast becomes your
      anchor. Their baseline sets the stage for you to look stronger, faster,
      sharper. Even average work looks exceptional when the anchor next to it
      is low.
    </p>

    <p>
      In practical terms:
    </p>

    <p>
      Pair your tasks with average performers. Be on teams where your
      strengths naturally jump out. Your brilliance becomes the “upgrade”
      their brain sees first.
    </p>

    <p>
      A decent film looks phenomenal if everything else that week is trash
      right? That’s anchoring bias. The brain compares by default. It cannot
      help it.
    </p>

    <p>
      <strong>THE GOAL:</strong>
    </p>

    <p>
      Set the anchor. Shape the comparison. Your work looks bigger when the
      reference point is smaller.
    </p>

    <p>
      <strong>7.&nbsp; SCARCITY PRINCIPLE</strong>
    </p>

    <p>
      We value what’s rare, not what’s good. Make your mix of skills feel
      scarce, not replaceable.
    </p>

    <p>
      Subtly remind leaders your skill mix is rare and time-sensitive. Say,{" "}
        “Few of us know this platform end-to-end. I’m training others to
        de-risk it.”
    </p>

    <p>
      Translation: <em>I’m rare. But I’m generous about it.</em>
    </p>

    <p>
      <strong>THE GOAL:</strong> They see promoting you as retention insurance.
      Your departure would create a critical gap.
    </p>

    <p>
      <strong>8.&nbsp; AUTHORITY SIGNALING</strong>
    </p>

    <p>
      Authority isn’t a title, it’s how you sound. Structure signals
      competence.
    </p>

    <p>
      Speak in frameworks (<em>“Three levers to fix this…”</em>). Use confident,
      brief summaries at the end of meetings.
    </p>

    <p>
      <strong>THE GOAL:</strong> People trust those who make complexity legible.
      You become the person who can be trusted with bigger, messier problems.
    </p>

    <p>
      <strong>9.&nbsp; ATTRIBUTION FRAMING</strong>
    </p>

    <p>
      People remember who framed the success story. In every achievement,
      position yourself as the driver, not a passenger.
    </p>

    <p>
        “I led the integration between A &amp; B to stabilize client
        onboarding.”
    </p>

    <p>
      Avoid: “We worked on…” or “The team did…”
    </p>

    <p>
      <strong>THE GOAL:</strong> At calibration, you want them to say,{" "}
      <em>“Oh yes, you led that,”</em> not{" "}
      <em>“Who did that again?”</em>
    </p>

    <p>
      Every career runs on two engines: effort and awareness. You’ve already
      got the first one.
    </p>

    <p>
      Now, when you add awareness, when you learn the psychology, the system
      starts moving in your favor.
    </p>
  </div>
);

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default PromotionPsychologyFunnelVideo;
